import { useTranslation } from 'react-i18next';
import { ControlPointDuplicate as ControlPointDuplicateIcon } from '@mui/icons-material';

import { DuplicateActionIconButton } from '@/components/actions';
import { ActionButton, type ActionButtonProps, type ActionIconButtonProps } from '@/components/buttons';
import {
  useNomenclatureStoreActions,
  useNomenclatureStoreContextWindowsState,
  useNomenclatureStoreIsContextWindowsVisible,
} from '@/components/nomenclature/useNomenclatureStore';
import { useShortcut } from '@/shared/hooks/useShortcut';

// ----------------------------------------------------------------------
// MAIN BUTTON

export const NomenclatureMainDuplicateButton = (props: ActionIconButtonProps) => {
  const { disabled } = props;

  const isContextWindowsVisible = useNomenclatureStoreIsContextWindowsVisible();
  const canOpenDuplicateModal = !disabled && !isContextWindowsVisible;

  const { openDuplicateModal } = useNomenclatureStoreActions();

  const handleOpen = () => {
    if (canOpenDuplicateModal) {
      openDuplicateModal();
    }
  };

  useShortcut({
    combination: 'ctrl + d',
    onPressed: () => {
      handleOpen();
    },
    isEnable: canOpenDuplicateModal,
  });

  return <DuplicateActionIconButton {...props} onClick={handleOpen} />;
};

// ----------------------------------------------------------------------
// INLINE BUTTON

export const NomenclatureInlineDuplicateButton = (props: ActionIconButtonProps) => {
  const { onClick } = props;

  const { openDuplicateModal } = useNomenclatureStoreActions();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    openDuplicateModal();
  };

  return <DuplicateActionIconButton {...props} onClick={handleClick} />;
};

// ----------------------------------------------------------------------
// MODAL BUTTON

export const NomenclatureModalDuplicateButton = (props: ActionButtonProps) => {
  const { disabled } = props;
  const { t } = useTranslation('nomenclature-page');

  const { isDuplicateModalVisible, isDeleteDialogVisible } = useNomenclatureStoreContextWindowsState();
  const canOpenDuplicateModal = !disabled && !isDuplicateModalVisible && !isDeleteDialogVisible;

  const { openDuplicateModal, closeEditModal } = useNomenclatureStoreActions();

  const handleOpen = () => {
    if (canOpenDuplicateModal) {
      openDuplicateModal();
      closeEditModal();
    }
  };

  useShortcut({
    combination: 'ctrl + d',
    onPressed: () => {
      handleOpen();
    },
    isEnable: canOpenDuplicateModal,
  });

  return (
    <ActionButton startIcon={<ControlPointDuplicateIcon fontSize="small" />} onClick={handleOpen} {...props}>
      {t('actions.duplicate')}
    </ActionButton>
  );
};
